"use client";

import isEmpty from "lodash/isEmpty";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import { VALID_LANGUAGES } from "@/constants/i18n";
import useCurrentUserManager from "@/hooks/use-current-user-manager";
import usePersistedBoundStore from "@/store/persisted-bound-store";
import { isAuthenticated } from "@/utils/auth";
import { cleanRouteParamFromLangPrefix, matchRouteParam } from "@/utils/route";

interface AuthenticatedGuardProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

const routePatternsThatShouldCheckAuth = [
  /^\/process\/.*$/, // Matches process detail routes
  /^\/subprocess\/.*$/, // Matches subprocess detail routes
  /^\/completed$/, // Matches completed processes list route
  /^\/cv\/.*$/, // Matches cv route

  /^\/enhance$/, // Matches enhance routes
  /^\/evaluation\/.*$/, // Matches evaluation routes
  /^\/expired$/, // Matches expired routes
  /^\/profile$/, // Matches profile routes
  /^\/settings\/.*$/, // Matches settings routes
  /^\/invitation\/.*$/, // Matches invitation routes
  /^\/jobs$/, // Matches jobboard routes
  /^\/faq$/, // Matches faq routes
  /^\/plans$/, // Matches checkout routes
  /^\/$/, // Matches root route
];

export function AuthenticatedGuard({ children, fallback = null }: AuthenticatedGuardProps) {
  const currentUserManager = useCurrentUserManager();
  const hasHydrated = usePersistedBoundStore.use.hasHydrated();
  const pathname = usePathname();
  const [isUserAuthenticated, setIsUserAuthenticated] = useState(false);
  const [isReady, setIsReady] = useState(false);
  // Clean the pathname from language prefix
  const cleanPath = cleanRouteParamFromLangPrefix(pathname, VALID_LANGUAGES);
  // Check if the current route matches any of the provided patterns
  const shouldCheckAuth =
    isEmpty(routePatternsThatShouldCheckAuth) ||
    matchRouteParam(cleanPath, routePatternsThatShouldCheckAuth);

  useEffect(() => {
    // Wait for store to hydrate before checking auth
    if (!hasHydrated) {
      return;
    }

    // If route doesn't need auth check, mark as ready
    if (!shouldCheckAuth) {
      setIsReady(true);
      return;
    }

    // If no user manager available yet, wait for it
    // This allows useAuthGuard to handle redirect when manager becomes available
    if (!currentUserManager) {
      // Set ready to true so useAuthGuard can handle the redirect
      setIsReady(true);
      setIsUserAuthenticated(false);
      return;
    }

    async function checkAuthentication() {
      try {
        const authenticated = await isAuthenticated(currentUserManager);
        setIsUserAuthenticated(authenticated);
      } catch (_error) {
        setIsUserAuthenticated(false);
        currentUserManager?.signoutRedirect();
      } finally {
        setIsReady(true);
      }
    }
    checkAuthentication();
  }, [currentUserManager, shouldCheckAuth, hasHydrated]);

  if (!isReady) {
    return <>{fallback}</>;
  }
  if (!shouldCheckAuth) {
    return <>{children}</>;
  }

  if (!isUserAuthenticated) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
}
